import { FormTable, SheetForm, TgkAmountObjectSource } from "../../interfaces/CoObject";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CoObjectSourcePanelStateData } from "../co-table-panel/CoObjectSourceTablePanel";
import { IChoiceGroupOption, IComboBoxOption } from "@fluentui/react";
import { FormTreeSelection } from "../../cpm/FormTree";
import { InputAreaSelectionEnum } from "../../../utils/constants/InputAreaSelectionEnum";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";

export interface ExcelComponentProps {
  isEdit?: boolean;
  objectSource: TgkAmountObjectSource;
  updateCoObjectSourceAmount: (obj: TgkAmountObjectSource, tabValid, modified: boolean) => void;
  validateSource: (obj: TgkAmountObjectSource) => ObjectPanelTabs;
  isFileExcelExisting?: boolean;
  fileExcelName?: string;
  updateFileExcelName?: (fileExcelName: string) => void;
  isComponentInstance?: boolean;
  isLocked?: boolean;
  originalSheetArea?: SheetForm;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  setIsOldSheetOrAreaSelected?: Dispatch<SetStateAction<boolean>>;
  setIsOldValueForAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isOldValueForAreaToHide?: boolean;
  hasSheetToBeSelected?: boolean;
  setHasSheetToBeSelected?: Dispatch<SetStateAction<boolean>>;
  isLoadingDataExcel?: boolean;
  setIsLoadingDataExcel?: Dispatch<SetStateAction<boolean>>;
  setIsLoadingSheetAndArea?: Dispatch<SetStateAction<boolean>>;
  setIsSheetChartAndAreaToHide?: Dispatch<SetStateAction<boolean>>;
  isSheetChartAndAreaToHide?: boolean;
  objectSourceImage?: string;
}

const useExcelAmountSourcePanel = (props: ExcelComponentProps) => {
  const {
    objectSource,
    updateCoObjectSourceAmount,
    validateSource,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    isComponentInstance,
    isLocked,
  } = props;
  const [stateData, setStateData] = React.useState<CoObjectSourcePanelStateData>();
  const [sheetsAreaChartMap] = useState(new Map<string, string[]>());
  const areaOptions: IChoiceGroupOption[] = [
    { key: InputAreaSelectionEnum.AREA_NAME.description, text: "Select area" },
    { key: InputAreaSelectionEnum.CELL.description, text: "Custom selection" },
  ];
  const [selectedAreaOption, setSelectedAreaOption] = useState("");
  const [isOldValueForAreaToHide, setIsOldValueForAreaToHide] = useState<boolean>(true);
  const [hasSheetToBeSelected, setHasSheetToBeSelected] = useState<boolean>(false);
  const [isLoadingDataExcel, setIsLoadingDataExcel] = useState<boolean>(false);

  useEffect(() => {
    objectSource.cell
      ? setSelectedAreaOption(InputAreaSelectionEnum.CELL.description)
      : setSelectedAreaOption(InputAreaSelectionEnum.AREA_NAME.description);
    setStateData({
      areasItems: [],
      loading: true,
    });
  }, []);

  const onFileClick = (formSelection: FormTreeSelection) => {
    sheetsAreaChartMap.clear();
    const formTable = {
      code: formSelection.formCode,
      desc: formSelection.formDesc,
      oid: formSelection.key,
      oidPath: formSelection.oidPath,
    } as FormTable;

    const obj = {
      ...objectSource,
      form: formTable,
      sheet: null,
      area: null,
      cell: null,
      path: formSelection.oidPath,
      dbApplication: { id: null, desc: null },
      dimensions: [],
    } as TgkAmountObjectSource;

    updateCoObjectSourceAmount(obj, validateSource(obj), true);
  };

  const initAreas = (sheet: SheetForm) => {
    if (sheetsAreaChartMap) {
      sheetsAreaChartMap.forEach((value: string[], key: string) => {
        if (sheet && sheet.code == key) {
          const areaMap: IComboBoxOption[] = value.map((area) => {
            return createIComboBoxFromAreas(area);
          });
          setStateData({
            ...stateData,
            areasItems: areaMap,
          });
        }
      });
    }
  };

  const createIComboBoxFromAreas = (areaValue: string): IComboBoxOption => {
    return {
      key: areaValue,
      text: areaValue,
    };
  };

  const selectSheetComboBox = (key?: string, text?: string) => {
    if (key === undefined) {
      const obj = {
        ...objectSource,
        sheet: null,
        area: null,
        cell: null,
        dbApplication: { id: null, desc: null },
        dimensions: [],
      } as TgkAmountObjectSource;
      updateCoObjectSourceAmount(obj, validateSource(obj), true);
      return;
    }
    const obj = {
      ...objectSource,
      sheet: { code: key, desc: text, uid: null },
      area: null,
      cell: null,
      dbApplication: { id: null, desc: null },
      dimensions: [],
    } as TgkAmountObjectSource;

    updateCoObjectSourceAmount(obj, validateSource(obj), true);
    initAreas({ code: key, desc: text } as SheetForm);
  };

  const changeAreaSelection = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    setSelectedAreaOption(option.key);
    const obj = {
      ...objectSource,
      area: null,
      cell: null,
      dbApplication: { id: null, desc: null },
      dimensions: [],
    } as TgkAmountObjectSource;
    updateCoObjectSourceAmount(obj, validateSource(obj), true);
  };

  const selectAreaComboBox = (key?: string) => {
    if (key === undefined) {
      const obj = {
        ...objectSource,
        area: { name: null },
        cell: objectSource.cell,
        dbApplication: { id: null, desc: null },
        dimensions: [],
      } as TgkAmountObjectSource;
      updateCoObjectSourceAmount(obj, validateSource(obj), true);
      return;
    }
    const obj = {
      ...objectSource,
      area: { name: key },
      cell: objectSource.cell,
      dbApplication: { id: null, desc: null },
      dimensions: [],
    } as TgkAmountObjectSource;
    updateCoObjectSourceAmount(obj, validateSource(obj), true);
  };

  const selectAmountFromExcelCell = (cellName: string) => {
    const obj = {
      ...objectSource,
      area: { name: null },
      cell: cellName,
      dbApplication: { id: null, desc: null },
      dimensions: [],
    } as TgkAmountObjectSource;
    updateCoObjectSourceAmount(obj, validateSource(obj), true);
  };

  return {
    objectSource,
    onFileClick,
    selectSheetComboBox,
    initAreas,
    sheetsAreaChartMap,
    selectedAreaOption,
    areaOptions,
    changeAreaSelection,
    selectAreaComboBox,
    selectAmountFromExcelCell,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    isComponentInstance,
    isLocked,
    setIsOldValueForAreaToHide,
    isOldValueForAreaToHide,
    hasSheetToBeSelected,
    setHasSheetToBeSelected,
    isLoadingDataExcel,
    setIsLoadingDataExcel,
  };
};

export default useExcelAmountSourcePanel;
